// @ts-nocheck
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Shield,
    ArrowLeft,
    Lock,
    Eye,
    Trash2,
    RefreshCw,
    Download,
    Smartphone,
    Cloud,
    Brain,
    CreditCard,
    AlertTriangle,
} from "lucide-react";

export default function PaceVibePrivacyPage() {
    useEffect(() => {
        document.title = "PaceVibe — Privacy Policy | Orion Intelligence";
        window.scrollTo(0, 0);
        return () => { document.title = "Orion Intelligence"; };
    }, []);

    // PaceVibe brand colors (matching the app)
    const lime = "#DFFF00";
    const orange = "#FF5722";

    const sectionStyle = {
        background: "rgba(255, 255, 255, 0.03)",
        borderColor: "rgba(255, 255, 255, 0.06)"
    };

    return (
        <div
            className="min-h-screen text-white"
            style={{
                background: `radial-gradient(ellipse at 30% 0%, rgba(223, 255, 0, 0.05), transparent 50%),
                             radial-gradient(ellipse at 70% 100%, rgba(255, 87, 34, 0.05), transparent 50%),
                             linear-gradient(170deg, #0F1115, #06080d)`
            }}
        >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-12"
                >
                    <Link
                        to="/pacevibe"
                        className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8"
                    >
                        <ArrowLeft size={16} />
                        Back to PaceVibe
                    </Link>

                    <div className="flex items-center gap-4 mb-6">
                        <div
                            className="p-3 rounded-2xl"
                            style={{
                                background: "rgba(223, 255, 0, 0.08)",
                                border: `1px solid rgba(223, 255, 0, 0.2)`
                            }}
                        >
                            <Shield size={28} style={{ color: lime }} />
                        </div>
                        <div>
                            <h1 className="text-3xl md:text-4xl font-black tracking-tight">
                                PaceVibe — Privacy Policy
                            </h1>
                            <p className="text-sm text-slate-400 mt-1 uppercase tracking-widest font-medium">
                                Orion Intelligence • Last updated: May 2026
                            </p>
                        </div>
                    </div>

                    <div className="h-px w-full" style={{
                        background: `linear-gradient(90deg, ${lime}55, transparent)`
                    }} />
                </motion.div>

                {/* Quick summary card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mb-10 p-6 rounded-2xl border"
                    style={{
                        background: "rgba(223, 255, 0, 0.04)",
                        borderColor: "rgba(223, 255, 0, 0.18)"
                    }}
                >
                    <h3 className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: lime }}>
                        TL;DR — In One Glance
                    </h3>
                    <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: lime }} />
                            <span><strong className="text-white">Local-first by design.</strong> Almost everything stays on your device.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: lime }} />
                            <span><strong className="text-white">No accounts. No tracking. No ads.</strong> We don't sell or share your data.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: lime }} />
                            <span><strong className="text-white">Payments via Google Play.</strong> We never see your card.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: lime }} />
                            <span><strong className="text-white">AI Coach is opt-in.</strong> Sends only anonymous race parameters when you trigger it.</span>
                        </li>
                    </ul>
                </motion.div>

                {/* Content */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-8"
                >
                    {/* 1. About */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">01</span>
                            About PaceVibe
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            PaceVibe is a race-strategy and fueling assistant for runners, developed and published by:
                        </p>
                        <div className="rounded-xl p-5 space-y-1.5 text-sm font-medium border border-white/5"
                            style={{ background: "rgba(255,255,255,0.02)" }}>
                            <p><span className="text-slate-500 inline-block w-28">Name:</span><span className="text-white">Δέλλιος Δημήτριος (Dimitrios Dellios)</span></p>
                            <p><span className="text-slate-500 inline-block w-28">Trading as:</span><span className="text-white">Orion Intelligence</span></p>
                            <p><span className="text-slate-500 inline-block w-28">Type:</span><span className="text-white">Sole Proprietorship (Ατομική Επιχείρηση)</span></p>
                            <p><span className="text-slate-500 inline-block w-28">ΓΕΜΗ:</span><span className="text-white">192625657000</span></p>
                            <p><span className="text-slate-500 inline-block w-28">ΑΦΜ:</span><span className="text-white">135602991</span></p>
                            <p><span className="text-slate-500 inline-block w-28">Address:</span><span className="text-white">Πολύγυρος, Χαλκιδική, Greece</span></p>
                            <p>
                                <span className="text-slate-500 inline-block w-28">Email:</span>
                                <a href="mailto:support@orionintelligence.net" className="hover:underline" style={{ color: lime }}>
                                    support@orionintelligence.net
                                </a>
                            </p>
                        </div>
                    </section>

                    {/* 2. Philosophy */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">02</span>
                            <Smartphone size={18} />
                            Philosophy: Local-First
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                            PaceVibe is built on a <strong className="text-white">local-first principle</strong>. Your personal and physiological
                            data (weight, age, heart rate, race parameters) are processed entirely on your device. We do not sell, rent,
                            or share your personal data with third parties for marketing purposes — and we never will.
                        </p>
                    </section>

                    {/* 3. Local data */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">03</span>
                            Data Processed Locally
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            The following data is entered voluntarily by you and processed solely on your device:
                        </p>
                        <ul className="space-y-2 text-sm text-slate-400 mb-4">
                            <li className="flex items-start gap-3 p-3 rounded-lg border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-slate-200">Body metrics:</strong> weight, height, age, gender, resting heart rate, body fat percentage</span>
                            </li>
                            <li className="flex items-start gap-3 p-3 rounded-lg border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-slate-200">Race parameters:</strong> target finish time, distance, elevation profile</span>
                            </li>
                            <li className="flex items-start gap-3 p-3 rounded-lg border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-slate-200">GPX route files:</strong> processed entirely client-side, never uploaded</span>
                            </li>
                            <li className="flex items-start gap-3 p-3 rounded-lg border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-slate-200">Nutrition preferences:</strong> gel brand selection, fueling timing</span>
                            </li>
                            <li className="flex items-start gap-3 p-3 rounded-lg border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-slate-200">Environmental inputs:</strong> temperature, humidity, wind conditions</span>
                            </li>
                        </ul>
                        <p className="text-slate-300 leading-relaxed text-sm">
                            This data is stored locally in your device's storage (localStorage / app sandbox) and is
                            <strong className="text-white"> permanently deleted when you uninstall the application or clear app data</strong>.
                        </p>
                    </section>

                    {/* 4. AI Coach */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">04</span>
                            <Brain size={18} />
                            AI Coach Feature
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            When you explicitly activate the AI Coach feature, the following <strong className="text-white">non-identifying race
                            parameters</strong> are transmitted to our backend server and forwarded to Anthropic's Claude API:
                        </p>
                        <ul className="space-y-2 text-sm text-slate-400 mb-4 pl-5 list-disc marker:text-lime-300">
                            <li>Race distance and target time</li>
                            <li>Body weight (kg) and age range</li>
                            <li>Weather conditions (temperature, humidity)</li>
                            <li>Nutrition brand preference</li>
                        </ul>
                        <p className="text-slate-300 leading-relaxed text-sm mb-4">
                            <strong className="text-white">No name, email, device ID, or direct personal identifiers</strong> are included
                            in these requests. Request logs are retained for a maximum of 30 days for abuse prevention and quota enforcement,
                            then automatically and permanently deleted.
                        </p>
                        <p className="text-slate-300 leading-relaxed text-sm">
                            Anthropic processes this data under their own privacy policy:{" "}
                            <a href="https://www.anthropic.com/legal/privacy" className="hover:underline"
                                style={{ color: lime }} target="_blank" rel="noopener noreferrer">
                                anthropic.com/legal/privacy
                            </a>.
                        </p>
                    </section>

                    {/* 5. Weather */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">05</span>
                            <Cloud size={18} />
                            Weather Data
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                            When you use GPX Race Mode with a race date, the GPS coordinates of the race start (extracted from your GPX file)
                            and the race date are sent to <strong className="text-white">OpenWeatherMap</strong> to retrieve a weather forecast.
                            No personal data is included in this request. See{" "}
                            <a href="https://openweathermap.org/privacy-policy" className="hover:underline"
                                style={{ color: lime }} target="_blank" rel="noopener noreferrer">
                                openweathermap.org/privacy-policy
                            </a>.
                        </p>
                    </section>

                    {/* 6. Purchases */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">06</span>
                            <CreditCard size={18} />
                            Purchases
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                            All Pro subscription purchases are processed exclusively by <strong className="text-white">Google Play Billing</strong>{" "}
                            (Google LLC). We receive only an anonymous entitlement token confirming your active subscription status.
                            We do <strong className="text-white">not</strong> have access to your payment method, card details, or Google account
                            information. Google's privacy policy applies to payment processing:{" "}
                            <a href="https://policies.google.com/privacy" className="hover:underline"
                                style={{ color: lime }} target="_blank" rel="noopener noreferrer">
                                policies.google.com/privacy
                            </a>.
                        </p>
                    </section>

                    {/* 7. GDPR Legal Basis */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">07</span>
                            Legal Basis (GDPR)
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            PaceVibe is designed as a local-first application — the vast majority of data never leaves your device and is
                            therefore not subject to data processing by Orion Intelligence. For the limited cases where data is transmitted
                            externally:
                        </p>
                        <div className="space-y-3">
                            <div className="flex gap-4 items-start p-4 rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="text-xs font-bold px-2 py-1 rounded-lg shrink-0 mt-0.5"
                                    style={{ background: "rgba(223, 255, 0, 0.1)", color: lime }}>
                                    6(1)(a)
                                </span>
                                <div>
                                    <h4 className="text-sm font-bold text-white mb-1">Consent</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed">
                                        AI Coach feature — you initiate each request explicitly.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start p-4 rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="text-xs font-bold px-2 py-1 rounded-lg shrink-0 mt-0.5"
                                    style={{ background: "rgba(223, 255, 0, 0.1)", color: lime }}>
                                    6(1)(b)
                                </span>
                                <div>
                                    <h4 className="text-sm font-bold text-white mb-1">Contractual Necessity</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed">
                                        Entitlement verification via Google Play for active subscribers.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start p-4 rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                <span className="text-xs font-bold px-2 py-1 rounded-lg shrink-0 mt-0.5"
                                    style={{ background: "rgba(223, 255, 0, 0.1)", color: lime }}>
                                    6(1)(f)
                                </span>
                                <div>
                                    <h4 className="text-sm font-bold text-white mb-1">Legitimate Interest</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed">
                                        Short-term abuse prevention logs (30-day maximum retention).
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 8. Rights */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">08</span>
                            Your Rights (GDPR)
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-5">
                            Under GDPR, you have the right to: access, rectification, erasure, restriction of processing, data portability,
                            and objection. Since almost all data is stored locally on your device,
                            <strong className="text-white"> most rights are exercised directly by editing or deleting data in-app, or uninstalling the app</strong>.
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {[
                                { icon: Eye, title: "Right of Access", desc: "Know what data we process about you." },
                                { icon: RefreshCw, title: "Right of Rectification", desc: "Correct inaccurate or incomplete data." },
                                { icon: Trash2, title: "Right to Erasure", desc: "Request deletion under applicable conditions." },
                                { icon: Lock, title: "Right of Restriction", desc: "Suspend processing in specific cases." },
                                { icon: Download, title: "Right to Portability", desc: "Receive data in a structured format." },
                                { icon: Shield, title: "Withdraw Consent", desc: "Stop AI Coach requests at any time." },
                            ].map((right, i) => (
                                <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-white/5" style={{ background: "rgba(255,255,255,0.02)" }}>
                                    <div className="p-2 rounded-lg shrink-0" style={{ background: "rgba(223, 255, 0, 0.06)" }}>
                                        <right.icon size={16} style={{ color: lime }} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-white">{right.title}</h4>
                                        <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{right.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-sm text-slate-400 leading-relaxed mt-5">
                            For server-side logs (AI Coach requests), submit a request to{" "}
                            <a href="mailto:support@orionintelligence.net" className="hover:underline" style={{ color: lime }}>
                                support@orionintelligence.net
                            </a>.
                            We respond within 30 days. EU residents may lodge a complaint with the Hellenic Data Protection Authority (HDPA):{" "}
                            <a href="https://www.dpa.gr" className="hover:underline" style={{ color: lime }}
                                target="_blank" rel="noopener noreferrer">dpa.gr</a>.
                        </p>
                    </section>

                    {/* 9. Retention */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">09</span>
                            Data Retention
                        </h2>
                        <ul className="space-y-3 text-sm text-slate-400">
                            <li className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-white">Local device data:</strong> retained until you uninstall the app or clear app data.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-white">AI Coach request logs:</strong> maximum 30 days, then permanently deleted.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lime }} />
                                <span><strong className="text-white">Purchase entitlement tokens:</strong> retained for the duration of the subscription.</span>
                            </li>
                        </ul>
                    </section>

                    {/* 10. Children */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">10</span>
                            Children's Privacy
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                            PaceVibe is not directed at children under 13 years of age. We do not knowingly collect personal data from
                            children. If you believe a child has submitted personal data through the AI Coach feature, contact us
                            immediately and we will delete any associated records.
                        </p>
                    </section>

                    {/* 11. Medical Disclaimer */}
                    <section className="rounded-2xl border p-6 md:p-8" style={{
                        background: "rgba(255, 87, 34, 0.05)",
                        borderColor: "rgba(255, 87, 34, 0.25)"
                    }}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: orange }}>
                            <span className="text-xs font-mono opacity-50 mr-1">11</span>
                            <AlertTriangle size={18} />
                            Medical Disclaimer
                        </h2>
                        <p className="leading-relaxed font-medium" style={{ color: "#ffd9c9" }}>
                            PaceVibe is <strong>not a medical device</strong> and does not constitute medical advice. Pace, fueling, and
                            hydration recommendations are general athletic guidance based on published sports science literature. Always
                            consult a qualified physician or sports medicine specialist before beginning a training programme or race
                            nutrition plan, particularly if you have any cardiovascular or metabolic condition.
                        </p>
                    </section>

                    {/* 12. Changes */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">12</span>
                            Changes to This Policy
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                            We may update this Privacy Policy to reflect changes in the app or applicable law. Material changes will be
                            notified via an in-app notice. Continued use of PaceVibe after the effective date constitutes acceptance of
                            the revised policy.
                        </p>
                    </section>

                    {/* 13. Contact */}
                    <section className="rounded-2xl border p-6 md:p-8" style={sectionStyle}>
                        <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: lime }}>
                            <span className="text-xs font-mono opacity-50 mr-1">13</span>
                            Contact
                        </h2>
                        <p className="text-slate-300 leading-relaxed mb-4">
                            Data protection enquiries:
                        </p>
                        <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-white/10"
                            style={{ background: "rgba(223, 255, 0, 0.04)" }}>
                            <span className="text-sm font-bold text-white">Email:</span>
                            <a href="mailto:support@orionintelligence.net"
                                className="text-sm font-medium hover:underline"
                                style={{ color: lime }}>
                                support@orionintelligence.net
                            </a>
                        </div>
                        <p className="text-sm text-slate-400 mt-4">
                            Orion Intelligence · Πολύγυρος, Χαλκιδική, Greece
                        </p>
                    </section>
                </motion.div>

                {/* Footer */}
                <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-slate-500">
                        © {new Date().getFullYear()} Orion Intelligence · ΓΕΜΗ 192625657000 · ΑΦΜ 135602991
                    </p>
                    <div className="flex gap-6 text-xs text-slate-500">
                        <Link to="/pacevibe" className="hover:text-white transition-colors">PaceVibe</Link>
                        <Link to="/privacy" className="hover:text-white transition-colors">Site Privacy</Link>
                        <Link to="/" className="hover:text-white transition-colors">Home</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
